import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router';
import { useNotifications } from '../contexts/NotificationContext';
import { Notification } from '../services/notificationService';
import './NotificationBell.css';

const NotificationBell: React.FC = () => {
  const {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const handleBellClick = () => {
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (notification.isRead === 0) {
      await markAsRead(notification.notificationId);
    }
    setIsOpen(false);
  };

  const handleMarkAllRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await markAllAsRead();
  };

  const getNotificationLink = (notification: Notification): string => {
    switch (notification.type) {
      case 'follow':
        return `/users/${notification.actorUsername}`;
      case 'comment':
      case 'favorite':
      case 'rating':
      case 'stream_upload':
        return notification.targetId ? `/streams/${notification.targetId}` : '#';
      default:
        return '#';
    }
  };

  const getNotificationIcon = (type: Notification['type']): string => {
    switch (type) {
      case 'follow':
        return '👤';
      case 'comment':
        return '💬';
      case 'favorite':
        return '❤️';
      case 'rating':
        return '⭐';
      case 'stream_upload':
        return '🎵';
      case 'mention':
        return '@';
      default:
        return '🔔';
    }
  };

  return (
    <div className="notification-bell" ref={dropdownRef}>
      <button
        className="notification-bell-button"
        onClick={handleBellClick}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button
                className="mark-all-read-button"
                onClick={handleMarkAllRead}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="notification-list">
            {loading && notifications.length === 0 ? (
              <div className="notification-loading">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">No notifications yet</div>
            ) : (
              notifications.slice(0, 10).map((notification) => (
                <Link
                  key={notification.notificationId}
                  to={getNotificationLink(notification)}
                  className={`notification-item ${notification.isRead === 0 ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon">
                    {notification.actorProfilePicture ? (
                      <img
                        src={notification.actorProfilePicture}
                        alt={notification.actorUsername}
                        className="notification-avatar"
                      />
                    ) : (
                      <span className="notification-type-icon">
                        {getNotificationIcon(notification.type)}
                      </span>
                    )}
                  </div>
                  <div className="notification-content">
                    <p className="notification-message">{notification.message}</p>
                    <span className="notification-time">{notification.timeAgo}</span>
                  </div>
                  {notification.isRead === 0 && (
                    <span className="notification-unread-dot" />
                  )}
                </Link>
              ))
            )}
          </div>

          {notifications.length > 0 && (
            <Link
              to="/notifications"
              className="notification-see-all"
              onClick={() => setIsOpen(false)}
            >
              See all notifications
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
